'use client';

import React, { useState, useEffect, forwardRef, isValidElement, useMemo } from 'react';
import { getPageMeta } from 'apis/page';
import { DOMPurify } from '@shared/get-html';
import { clsx } from 'clsx';
import Link from 'next/link';
import { FEATURE_FLAG_KEYS, UI_LINK_TYPE_ENUM } from 'shop-ui/constants';
import { type BasicShopUIPropTypes } from 'shop-ui/types';
import { useBuyerBrandStore } from 'app/store/brand';
import './Button.scss';

interface ButtonProps extends BasicShopUIPropTypes {
    content: string;
    icon: {
        iconClassName?: string;
        iconPosition?: string;
    };
}

export const Button = forwardRef<HTMLDivElement, ButtonProps>((props, ref) => {
    const { className, content, children, style, styleMap, isEditingMode, icon } = props;

    const [href, setHref] = useState<string>('');

    const cache = useBuyerBrandStore((api) => api.cache);

    const updateSegmentCache = useBuyerBrandStore((api) => api.setPageSegment);

    const target = useMemo(() => {
        if (styleMap) {
            return styleMap[FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW]
                ? '_blank'
                : '_self';
        }

        return '_self';
    }, [styleMap]);

    useEffect(() => {
        if (
            styleMap &&
            styleMap[FEATURE_FLAG_KEYS.UI_LINK_TYPE] === UI_LINK_TYPE_ENUM.PAGE &&
            styleMap[FEATURE_FLAG_KEYS.UI_LINK]
        ) {
            const pageId = styleMap[FEATURE_FLAG_KEYS.UI_LINK];

            const pageSegment = cache.pageSegment[pageId];

            if (pageSegment) {
                const _href = pageSegment === 'home' ? '/' : `/${pageSegment}`;

                setHref(DOMPurify.sanitize(_href));
            } else if (isEditingMode) {
                getPageMeta({ pageIds: [pageId] })
                    .then((res) => {
                        if (res?.data) {
                            updateSegmentCache((prev) => ({
                                ...prev,
                                [pageId]: res.data[0].segment,
                            }));
                        }
                    })
                    .catch(() => {
                        setHref('/');
                    });
            } else {
                setHref('/');
            }
        }

        if (
            styleMap &&
            styleMap[FEATURE_FLAG_KEYS.UI_LINK_TYPE] === UI_LINK_TYPE_ENUM.ANCHOR
        ) {
            setHref(`#${styleMap[FEATURE_FLAG_KEYS.UI_LINK]}`);
        }

        if (
            styleMap &&
            styleMap[FEATURE_FLAG_KEYS.UI_LINK_TYPE] === UI_LINK_TYPE_ENUM.URL
        ) {
            setHref(styleMap[FEATURE_FLAG_KEYS.UI_LINK]);
        }
    }, [styleMap, cache, updateSegmentCache, isEditingMode]);

    return (
        <div
            ref={ref}
            style={style}
            className={clsx('button-container', isEditingMode && 'is-editing')}
        >
            <Link
                href={href ?? '/'}
                target={target}
                className={clsx('button', className)}
            >
                {icon.iconClassName && icon.iconPosition === 'left' && (
                    <i
                        className={clsx(
                            'fi',
                            (!!children || !!content) && 'button-left-icon',
                            icon.iconClassName
                        )}
                    />
                )}
                {isValidElement(children) ? children : content}
                {icon.iconClassName && icon.iconPosition === 'right' && (
                    <i
                        className={clsx(
                            'fi',
                            (!!children || !!content) && 'button-right-icon',
                            icon.iconClassName
                        )}
                    />
                )}
            </Link>
        </div>
    );
});

Button.displayName = 'Button';
